import { useMemo } from 'react';
import { Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { PeoplePicker, initialsOf } from '@/components/projects/PeoplePicker';
import { useProfiles } from '@/features/users/hooks';
import { useAllCardAssignments } from '@/features/expenses/cardAssignments';
import {
  useAssignTransaction,
  useTransactionAssignments,
  useUnassignTransaction,
} from '@/features/expenses/transactionAssignments';

/**
 * Super_admin-only "Shared with" editor for a single transaction.
 *
 * The cardholder mapping (card_assignments) is always the default and is
 * rendered as locked chips that can't be removed here — to change who owns a
 * card, the super_admin uses the Card mapping page. On top of that default,
 * this card lets the super_admin attach any other active person to this one
 * transaction (transaction_assignments), which is exactly what you want for an
 * expense on a general company card: the cardholder still sees it, plus the
 * employee who actually made the purchase can now add the receipt + details.
 *
 * Render this only for super_admin — RLS makes the write a no-op for anyone
 * else, and the cardholder roster (useAllCardAssignments) is super_admin-only
 * anyway.
 */
export function TransactionSharingCard({
  transactionId,
  cardId,
  disabled = false,
}: {
  transactionId: string;
  cardId: string | null;
  disabled?: boolean;
}) {
  const { data: profiles, isLoading: profilesLoading } = useProfiles();
  const { data: cardAssignments, isLoading: cardLoading } = useAllCardAssignments();
  const { data: assignments, isLoading: assignmentsLoading } =
    useTransactionAssignments(transactionId);
  const assign = useAssignTransaction();
  const unassign = useUnassignTransaction();

  const eligibleProfiles = useMemo(
    () =>
      (profiles ?? [])
        .filter((p) => p.is_active)
        .map((p) => ({
          id: p.id,
          full_name: p.full_name,
          email: p.email,
          avatar_url: p.avatar_url,
        })),
    [profiles],
  );

  // Cardholders for this transaction's card — the always-on default. Rendered
  // as locked chips so the super_admin sees who already has access by virtue
  // of owning the card.
  const cardHolderIds = useMemo(() => {
    if (!cardId) return [] as string[];
    return (cardAssignments ?? [])
      .filter((a) => a.card_id === cardId)
      .map((a) => a.profile_id);
  }, [cardAssignments, cardId]);

  const assignedIds = useMemo(
    () => (assignments ?? []).map((a) => a.profile_id),
    [assignments],
  );

  // The picker shows the cardholders (locked) + the explicitly-shared people.
  const value = useMemo(
    () => Array.from(new Set([...cardHolderIds, ...assignedIds])),
    [cardHolderIds, assignedIds],
  );

  const isLoading = profilesLoading || cardLoading || assignmentsLoading;
  const busy = assign.isPending || unassign.isPending;

  const onChange = async (next: string[]) => {
    const nextSet = new Set(next);
    const assignedSet = new Set(assignedIds);
    const cardHolderSet = new Set(cardHolderIds);

    const toAdd = next.filter(
      (id) => !assignedSet.has(id) && !cardHolderSet.has(id),
    );
    const toRemove = assignedIds.filter((id) => !nextSet.has(id));

    try {
      for (const profile_id of toAdd) {
        await assign.mutateAsync({ transaction_id: transactionId, profile_id });
      }
      for (const profile_id of toRemove) {
        await unassign.mutateAsync({ transaction_id: transactionId, profile_id });
      }
      if (toAdd.length > 0) toast.success('Shared with the selected people.');
      else if (toRemove.length > 0) toast.success('Sharing updated.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update sharing');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          Shared with
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <>
            <PeoplePicker
              profiles={eligibleProfiles}
              value={value}
              onChange={onChange}
              lockedIds={cardHolderIds}
              placeholder="Add people to this transaction…"
              disabled={disabled || busy}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Compact read-only banner for the employee-facing Expense Detail page: tells
 * a per-transaction assignee that an admin shared this row with them (it isn't
 * on a card they own). Renders nothing when there's nothing to say.
 */
export function SharedWithYouNote({
  isCardholder,
  isAssignee,
}: {
  isCardholder: boolean;
  isAssignee: boolean;
}) {
  if (isCardholder || !isAssignee) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-atmosphere-dark/30 bg-atmosphere-dark/10 px-4 py-3 text-sm">
      <Users className="mt-0.5 h-4 w-4 shrink-0 text-atmosphere-dark" />
      <p className="font-medium text-foreground">Shared with you.</p>
    </div>
  );
}

/** Small avatar-chip list reused if we ever want a static roster display. */
export function AssigneeAvatars({
  profiles,
}: {
  profiles: Array<{
    id: string;
    full_name: string | null;
    email: string;
    avatar_url?: string | null;
  }>;
}) {
  if (profiles.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {profiles.map((p) => (
        <Badge
          key={p.id}
          variant="secondary"
          className="flex items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-2 text-xs font-normal"
        >
          <Avatar className="h-5 w-5">
            {p.avatar_url && <AvatarImage src={p.avatar_url} alt="" />}
            <AvatarFallback className="text-[9px]">{initialsOf(p)}</AvatarFallback>
          </Avatar>
          <span className="truncate text-foreground">{p.full_name ?? p.email}</span>
        </Badge>
      ))}
    </div>
  );
}
